import React from 'react';
import { NotificationConfiguration } from '../../../../types/workflow';

interface NotificationConfigProps {
  configuration: NotificationConfiguration;
  onChange: (config: NotificationConfiguration) => void;
  isReadOnly: boolean;
}

const NotificationConfig: React.FC<NotificationConfigProps> = ({
  configuration,
  onChange,
  isReadOnly,
}) => {
  const updateConfig = (field: keyof NotificationConfiguration, value: any) => {
    onChange({
      ...configuration,
      [field]: value,
    });
  };

  const toggleChannel = (channel: 'email' | 'sms' | 'inApp') => {
    const channels = configuration.channels || [];
    if (channels.includes(channel)) {
      updateConfig('channels', channels.filter(c => c !== channel));
    } else {
      updateConfig('channels', [...channels, channel]);
    }
  };

  return (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700">
          Template
        </label>
        <select
          value={configuration.template || ''}
          onChange={(e) => updateConfig('template', e.target.value)}
          disabled={isReadOnly}
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm disabled:bg-gray-100"
        >
          <option value="">Select a template</option>
          <option value="approval_request">Approval Request</option>
          <option value="approval_granted">Approval Granted</option>
          <option value="approval_denied">Approval Denied</option>
          <option value="task_assigned">Task Assigned</option>
          <option value="task_completed">Task Completed</option>
          <option value="workflow_started">Workflow Started</option>
          <option value="workflow_completed">Workflow Completed</option>
          <option value="workflow_failed">Workflow Failed</option>
          <option value="custom">Custom Message</option>
        </select>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700">
          Recipients
        </label>
        <input
          type="text"
          value={configuration.recipients?.join(', ') || ''}
          onChange={(e) => 
            updateConfig('recipients', e.target.value.split(',').map(s => s.trim()).filter(Boolean))
          }
          disabled={isReadOnly}
          placeholder="Enter comma-separated emails or user IDs"
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm disabled:bg-gray-100"
        />
        <p className="mt-1 text-xs text-gray-500">
          Use special keywords: @requester, @assignee, @approvers, @watchers
        </p>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Notification Channels
        </label>
        <div className="space-y-2">
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={configuration.channels?.includes('email') || false}
              onChange={() => toggleChannel('email')}
              disabled={isReadOnly}
              className="rounded border-gray-300 text-primary-600 shadow-sm focus:border-primary-500 focus:ring-primary-500 disabled:bg-gray-100"
            />
            <span className="ml-2 text-sm text-gray-700">Email</span>
          </label>
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={configuration.channels?.includes('sms') || false}
              onChange={() => toggleChannel('sms')}
              disabled={isReadOnly}
              className="rounded border-gray-300 text-primary-600 shadow-sm focus:border-primary-500 focus:ring-primary-500 disabled:bg-gray-100"
            />
            <span className="ml-2 text-sm text-gray-700">SMS</span>
          </label>
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={configuration.channels?.includes('inApp') || false}
              onChange={() => toggleChannel('inApp')}
              disabled={isReadOnly}
              className="rounded border-gray-300 text-primary-600 shadow-sm focus:border-primary-500 focus:ring-primary-500 disabled:bg-gray-100"
            />
            <span className="ml-2 text-sm text-gray-700">In-App Notification</span>
          </label>
        </div>
      </div>
    </div>
  );
};

export default NotificationConfig;